import React, { useMemo } from "react";
import { endOfYesterday, parseISO, startOfDay, subMonths } from "date-fns";
import DirectoryPicker from "components/shared/DirectoryPicker";
import Label from "components/shared/form/Label";
import ReactSelect from "react-select";
import DateRangeInput, {
  dateRangeLast3Months,
} from "components/shared/DateRangeInput";
import { isArray, isEmpty, keys, pickBy, memoize } from "lodash";
import { useSearchParams } from "helpers/tixxt-router";

type EventType = (typeof EVENT_TYPES)[number];
type AuditLogFilter = {
  event_types?: EventType[];
  actor_id?: string;
  membership_id?: string;
  start_date?: string;
  end_date?: string;
  date_option?: string;
  details?: string;
};

const DEFAULT_START_DATE = startOfDay(subMonths(endOfYesterday(), 3));
const DEFAULT_END_DATE = endOfYesterday();

const EVENT_TYPES = [
  "GroupJoin",
  "GroupLeave",
  "MembershipDestroy",
  "GroupAdminPermissionCreate",
  "GroupAdminPermissionDestroy",
  "PolicySettingUpdate",
  "FilePublish",
  "FileDestroy",
  "FileMove",
];

const getEventTypeOptions = memoize(() =>
  EVENT_TYPES.map((type) => ({
    label: I18n.t(`js.audit_logs.network_events.${type.toLowerCase()}.title`),
    value: type,
  })),
);

function parseFilter(filterString: string | null): AuditLogFilter {
  if (!filterString) return {};
  try {
    const decodedFilter = atob(filterString);
    return JSON.parse(decodedFilter);
  } catch (e) {
    console.error("Error parsing filter:", e);
    return {};
  }
}

export default function AuditLogFilter() {
  const [searchParams] = useSearchParams();
  const eventTypeOptions = getEventTypeOptions();
  const currentFilter = parseFilter(searchParams.get("filter"));

  const selectedEventTypes = eventTypeOptions.filter(
    ({ value }) => currentFilter.event_types?.includes(value),
  );

  const startDate = useMemo(() => {
    if (!currentFilter.start_date) return DEFAULT_START_DATE;
    try {
      return parseISO(currentFilter.start_date);
    } catch (e) {
      console.error("Error parsing start date:", e);
      return DEFAULT_START_DATE;
    }
  }, [currentFilter.start_date]);

  const endDate = useMemo(() => {
    if (!currentFilter.end_date) return DEFAULT_END_DATE;
    try {
      return parseISO(currentFilter.end_date);
    } catch (e) {
      console.error("Error parsing end date:", e);
      return DEFAULT_END_DATE;
    }
  }, [currentFilter.end_date]);

  const updateFilter = (changedFilter: Record<string, any>) => {
    const newFilter = { ...currentFilter, ...changedFilter };
    const cleanFilter = pickBy(newFilter, (value) => {
      if (isArray(value)) return value.length > 0;
      return value && !isEmpty(value);
    });

    let currentPath = window.location.pathname;
    if (keys(cleanFilter).length > 0) {
      const base64Filter = btoa(JSON.stringify(cleanFilter));
      currentPath = `${currentPath}?filter=${base64Filter}`;
    }
    // Turbo.visit with frame doesn update url, so we do it manually
    // Without targeting the frame the stimulus controller gets disconnected and immediately reconnected,
    // which results in flickering filter component
    window.Turbo?.visit(currentPath, {
      frame: "audit-log--events",
    });
    history.replaceState(null, "", currentPath);
  };

  const handleActorChange = (value: any) => {
    updateFilter({ actor_id: value?.id || "" });
  };

  const handleMembershipChange = (value: any) => {
    updateFilter({ membership_id: value?.id || "" });
  };

  const handleDateRangeChange = (value: any) => {
    updateFilter({
      start_date: value.start,
      end_date: value.end,
      date_option: value.option,
    });
  };

  const handleDetailsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateFilter({ details: event.target.value });
  };

  const handleEventTypesChange = (options: any) => {
    const eventTypes = options?.map((opt: any) => opt.value) || [];
    updateFilter({ event_types: eventTypes });
  };

  return (
    <div className="form-horizontal mt-4 mb-6">
      <div className="control-group">
        <Label label={I18n.t("js.audit_logs.filter.date_range")} />
        <div className="controls">
          <DateRangeInput
            value={{
              start: startDate,
              end: endDate,
            }}
            defaultOption={currentFilter.date_option || dateRangeLast3Months}
            onChange={handleDateRangeChange}
          />
        </div>
      </div>
      <div className="control-group">
        <Label label={I18n.t("js.audit_logs.filter.details")} />
        <div className="controls">
          <input
            type="text"
            className="form-control"
            value={currentFilter.details || ""}
            onChange={handleDetailsChange}
            placeholder={I18n.t("js.audit_logs.filter.details_placeholder")}
          />
        </div>
      </div>
      <div className="control-group">
        <Label label={I18n.t("js.audit_logs.filter.event_types")} />
        <ReactSelect
          closeMenuOnSelect
          id="event_types"
          className="controls"
          options={eventTypeOptions}
          value={selectedEventTypes}
          placeholder="Aktion auswählen..."
          onChange={handleEventTypesChange}
          isSearchable
          isClearable
          isMulti
        />
      </div>
      <div className="control-group">
        <Label label={I18n.t("js.audit_logs.filter.actor")} />
        <div className="controls">
          <DirectoryPicker
            value={
              currentFilter.actor_id
                ? [{ id: currentFilter.actor_id, type: "member" }]
                : undefined
            }
            onChange={handleActorChange}
            endpoint="/directory/members"
          />
        </div>
      </div>
      <div className="control-group">
        <Label label={I18n.t("js.audit_logs.filter.membership")} />
        <div className="controls">
          <DirectoryPicker
            value={
              currentFilter.membership_id
                ? [{ id: currentFilter.membership_id, type: "member" }]
                : undefined
            }
            onChange={handleMembershipChange}
            endpoint="/directory/members"
          />
        </div>
      </div>
      {!isEmpty(currentFilter) ? (
        <div className="flex items-end ml-auto">
          <a className="btn btn-primary" href="/administration/audit_log">
            {I18n.t("js.audit_logs.filter.filter.reset")}
          </a>
        </div>
      ) : null}
    </div>
  );
}
